import { useEffect } from 'react';
import { injectSchemaMicrodata, generateSchemaMicrodata, createSchemaScriptTag } from '../utils/schemaMicrodata';

export const useSchemaMicrodata = (type, data, dependencies = []) => {
  useEffect(() => {
    if (!type || !data) return;

    const schemaData = generateSchemaMicrodata(type, data);
    if (schemaData) {
      injectSchemaMicrodata(schemaData);
      
      // Also add to window object for potential server-side access
      if (typeof window !== 'undefined') {
        window.__SCHEMA_DATA__ = schemaData;
      }
    }

    // Cleanup function to remove schema when component unmounts
    return () => {
      const existingScripts = document.querySelectorAll('script[type="application/ld+json"]');
      existingScripts.forEach(script => script.remove());
    };
  }, [type, data, ...dependencies]);
};

export const useMultipleSchemaMicrodata = (schemas) => {
  useEffect(() => {
    if (!schemas || schemas.length === 0) return;

    // Remove existing schema script tags
    const existingScripts = document.querySelectorAll('script[type="application/ld+json"]');
    existingScripts.forEach(script => script.remove());

    // Inject all schema data
    const allSchemas = [];
    schemas.forEach(schema => {
      if (schema.type && schema.data) {
        const schemaData = generateSchemaMicrodata(schema.type, schema.data);
        if (schemaData) {
          injectSchemaMicrodata(schemaData);
          allSchemas.push(schemaData);
        }
      }
    });

    // Store all schemas in window object
    if (typeof window !== 'undefined' && allSchemas.length > 0) {
      window.__SCHEMA_DATA__ = allSchemas;
    }

    // Cleanup function
    return () => {
      const existingScripts = document.querySelectorAll('script[type="application/ld+json"]');
      existingScripts.forEach(script => script.remove());
    };
  }, [schemas]);
};

// Enhanced hook for better SEO
export const useEnhancedSchemaMicrodata = (type, data, dependencies = []) => {
  useEffect(() => {
    if (!type || !data) return;

    const schemaData = generateSchemaMicrodata(type, data);
    if (schemaData) {
      // Inject immediately for better SEO
      injectSchemaMicrodata(schemaData);
      
      // Store in window for potential server-side access
      if (typeof window !== 'undefined') {
        window.__SCHEMA_DATA__ = schemaData;
        
        // Dispatch custom event for potential server-side rendering
        window.dispatchEvent(new CustomEvent('schemaDataUpdated', {
          detail: { type, data: schemaData }
        }));
      }
      
      // Also add to document as a data attribute for crawlers
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schemaData);
      script.setAttribute('data-schema-type', type);
      document.head.appendChild(script);
    }

    return () => {
      const existingScripts = document.querySelectorAll('script[type="application/ld+json"]');
      existingScripts.forEach(script => script.remove());
    };
  }, [type, data, ...dependencies]);
}; 